import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity } from 'react-native-web';
import { Dialog } from './Dialog';
import { isEmpty } from '../ui/utils';
import { useApi } from '../hooks';
import { LayoutBox } from './Layout';

const CircularProgress = ({ progress, onExpand }) => {
  const isActive = progress > 0 && progress < 100;

  const styles = {
    container: {
      width: '50px',
      height: '50px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      zIndex: 1000,
    },
    svg: {
      position: 'relative',
      width: '100%',
      height: '100%',
    },
    circleBackground: {
      fill: 'none',
      stroke: '#e0e0e0',
      strokeWidth: '10',
    },
    circleProgress: {
      fill: 'none',
      stroke: isActive ? '#28a745' : '#e0e0e0',
      strokeWidth: '10',
      strokeLinecap: 'round',
      strokeDasharray: '251.2',
      strokeDashoffset: `${251.2 - (251.2 * progress) / 100}`,
      transition: 'stroke-dashoffset 0.5s ease',
    },
    shadow: {
      filter: 'url(#mce-shadow)',
    },
    progressText: {
      position: 'absolute',
      fontSize: '12px',
      fontWeight: 'bold',
      color: isActive ? '#28a745' : '#999',
    },
  };

  return (
    <TouchableOpacity onPress={onExpand}>
      <View style={styles.container}>
        <svg style={styles.svg} viewBox="0 0 100 100">
          <defs>
            <filter id="mce-shadow" x="-5%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
              <feOffset dx="0" dy="2" result="offsetblur" />
              <feFlood floodColor="rgba(0, 0, 0, 0.3)" />
              <feComposite in2="offsetblur" operator="in" />
              <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <circle cx="50" cy="50" r="40" style={styles.circleBackground} />
          <circle cx="50" cy="50" r="40" style={{ ...styles.circleProgress, ...styles.shadow }} />
        </svg>
        <Text style={styles.progressText}>{progress}%</Text>
      </View>
    </TouchableOpacity>
  );
};

const MceMacavityTracking = ({ mceId, mceType, onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [startedAt, setStartedAt] = useState();
  const [showModal, setShowModal] = useState(false);
  const [ecsArnId, setEcsArnId] = useState(null);
  const { getMceJobStatus } = useApi();

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const handleStatusUpdate = useCallback(
    ({ taskStatus, systemTime }) => {
      const formattedTime = formatTime(new Date(systemTime));

      if (taskStatus === 'PENDING' && !startedAt) {
        setStartedAt(`Started - ${formattedTime}`);
      }

      if (taskStatus === 'RUNNING') {
        setStatusText(`Running – ${formattedTime}`);
      }
    },
    [startedAt]
  );

  const handleStop = useCallback(
    (response) => {
      if (response?.taskStatus === 'STOPPED') {
        setProgress(100);
        setStatusText(`Complete – ${formatTime(new Date(response.systemTime))}`);
        setTimeout(() => {
          setShowModal(false);
          setProgress(0);
          setStartedAt(null);
          setStatusText('');
          setEcsArnId(null);
          if (onComplete) {
            onComplete();
          }
        }, 3000);
      } else {
        // No active task found - reset to idle state
        setProgress(0);
        setStartedAt(null);
        setStatusText('');
        setEcsArnId(null);
      }
    },
    [onComplete]
  );

  const updateProgress = useCallback((response) => {
    const status = response.taskStatus;
    const systemTime = response.systemTime;

    setProgress((prevProgress) => {
      let newProgress = prevProgress;

      if (newProgress < 50 && status === 'RUNNING') {
        newProgress = 50;
      } else if (newProgress < 80 && status === 'RUNNING') {
        newProgress = Math.min(newProgress + 2, 80);
      } else if (status === 'PENDING') {
        newProgress = Math.min(newProgress + 2, 50);
      } else {
        newProgress = Math.min(newProgress + 2, 80);
      }

      return newProgress;
    });

    // Update status text outside of setProgress to avoid batching issues
    if (status !== 'PENDING') {
      setStatusText(`Running – ${formatTime(new Date(systemTime))}`);
    }
  }, []);

  // Continuous polling effect - only for locationChange MCEs
  useEffect(() => {
    if (!mceId || mceType !== 'locationChange') return;

    let currentEcsArnId = ecsArnId;

    const poll = async () => {
      try {
        const response = await getMceJobStatus(mceId, currentEcsArnId || 'none');

        if (isEmpty(response)) {
          // No active task - reset to idle
          handleStop(null);
          return;
        }

        if (response.taskStatus === 'STOPPED') {
          handleStop(response);
          return;
        }

        // Active task found
        if (!currentEcsArnId && response.ecsArnId) {
          currentEcsArnId = response.ecsArnId;
          setEcsArnId(response.ecsArnId);
          setProgress(20); // Initial progress when task found
        }

        handleStatusUpdate(response);
        updateProgress(response);
      } catch (error) {
        console.error('Error polling MCE job status:', error);
      }
    };

    // Initial poll
    poll();

    // Set up interval
    const intervalId = setInterval(poll, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [mceId, mceType, getMceJobStatus, handleStatusUpdate, handleStop, updateProgress, ecsArnId]);

  const handleExpand = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  // Only show for locationChange MCEs
  if (mceType !== 'locationChange') {
    return null;
  }

  const isActive = progress > 0 && progress < 100;

  return (
    <>
      {showModal ? (
        <Dialog open={true} heading={'Macavity Tracking'} onClose={handleClose} hideExitButton={true}>
          <View style={{ width: '100%', paddingTop: '3px' }}>
            <View
              style={{
                height: 15,
                backgroundColor: '#e0e0e0',
                borderRadius: 10,
                overflow: 'hidden',
                width: '100%',
                marginBottom: 10,
              }}
            >
              <View
                style={{
                  height: '100%',
                  backgroundColor: '#28a745',
                  width: `${progress}%`,
                  transition: 'width 0.4s ease',
                  animation: progress === 100 ? 'none' : 'pulse-fade 1.5s ease-in-out infinite',
                  boxShadow: '0 0 10px rgba(40, 167, 69, 0.5)',
                }}
              />
            </View>
            <LayoutBox row justifyContent="space-between">
              <Text>{startedAt ? startedAt : isActive ? `Started - ${formatTime(new Date())}` : 'No active job'}</Text>
              {statusText ? <Text>{statusText}</Text> : null}
            </LayoutBox>
          </View>
        </Dialog>
      ) : (
        <CircularProgress progress={progress} onExpand={handleExpand} />
      )}
    </>
  );
};

export default MceMacavityTracking;
